import React, { useState, useRef, useEffect, Dispatch, SetStateAction, useCallback } from 'react'
import styled from 'styled-components'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Dropdown from 'components/Dropdown'
import Search from 'components/Search'

import AdvancedFilters from 'pages/Customers/CustomerSearch/AdvancedFilters'
import { Selection } from 'pages/Customers/CustomerSearch/enums'

import GA4 from 'helpers/ga4'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  query?: string;
  filters?: string;
  onSearch: (arg1: string, arg2: string) => void;
  className?: string;
  showSpinner: boolean
}

const CustomerSearch = ({ query = '', filters = '', onSearch, className = '', showSpinner = false }: Props) => {
  const [inputValue, setInputValue] = useState(query)
  const [selectedFilters, setSelectedFilters] = useState((filters || '').split(',').filter(Boolean))
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setSelectedFilters((filters || '').split(',').filter(Boolean))
    setInputValue(query)
  }, [filters, query])

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [searchInputRef])

  const previouslySelected = (oldFilters: string[], group: { filters: string[]; rule: Selection }) => {
    let existingLabelFilter: string | undefined

    oldFilters.forEach((oldFilter) => {
      if (oldFilter !== 'not_enrolled') {
        group.filters.forEach(filter => {
          if (filter === oldFilter) existingLabelFilter = filter
        })
      }
    })

    if (existingLabelFilter !== undefined) {
      oldFilters = oldFilters.filter(oldFilter => oldFilter !== existingLabelFilter)
    }
    return oldFilters
  }

  const applyFiltersCallback = useCallback(() => {
    if (inputValue !== '') GA4.sendEvent({ category: 'Customer List Page', action: 'Execute Search', label: inputValue })
    onSearch(inputValue, selectedFilters.join(','))
  }, [inputValue, selectedFilters, onSearch])

  const applyFiltersAndCloseCallback = useCallback(() => {
    if (selectedFilters.length > 0) {
      GA4.sendEvent({ category: 'Customer List Page', action: 'Click Apply and Search', label: selectedFilters.join(',') })
    }
    onSearch(inputValue, selectedFilters.join(','))
    setIsDropdownOpen(false)
  }, [inputValue, selectedFilters, onSearch])

  const clearFiltersAndCloseCallback = useCallback(() => {
    setSelectedFilters([])
    setIsDropdownOpen(false)
  }, [])

  const toggleFilterCallback = useCallback((group: { rule: Selection; filters: string[] }, filter: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedFilters((oldFilters) => oldFilters.filter(oldFilter => oldFilter !== filter))
    } else {
      setSelectedFilters((oldFilters) => {
        if (group.rule === Selection.SINGLE && filter !== 'not_enrolled') {
          oldFilters = previouslySelected(oldFilters, group)
        }

        return [...oldFilters, filter]
      })
    }

    searchInputRef.current?.focus()
  }, [])

  const onCloseDropDownCallback = useCallback((event?: React.MouseEvent | React.KeyboardEvent, isClickOutsideDropdown?: boolean) => {
    if (isClickOutsideDropdown && isDropdownOpen) {
      setSelectedFilters((filters || '').split(',').filter(Boolean))
      onSearch(inputValue, filters)
    }
    setIsDropdownOpen(false)
  }, [isDropdownOpen, inputValue, filters, onSearch])

  return (
    <S.Container className={className}>
      <S.SearchContainer>
        <S.FilterIcon
          className="icon-filter"
          onClick={() => {
            setIsDropdownOpen((oldIsDropdownOpen) => !oldIsDropdownOpen)
            searchInputRef.current?.focus()
          }}
          anyFilterSelected={selectedFilters.length > 0}
          role="button"
          aria-label="filters"
        >
          Filter
        </S.FilterIcon>

        <S.Search
          ref={searchInputRef}
          ariaLabel="customer search"
          placeholder="Customer name, address, phone number, email, etc."
          value={inputValue}
          showClearIcon={inputValue !== ''}
          onClickClearIcon={() => {
            setInputValue('')
            searchInputRef.current?.focus()
          }}
          onChange={setInputValue}
          onSearch={() => applyFiltersCallback()}
          showSpinner={showSpinner}
        />
      </S.SearchContainer>

      <S.Dropdown isOpen={isDropdownOpen} onClose={onCloseDropDownCallback} ariaLabelsToIgnoreOnOutsideClick={['search', 'filters']}>
        <S.DropdownContent>
          <S.ScrollableFilters>
            <AdvancedFilters {...{ selectedFilters, toggleFilterCallback }} />
          </S.ScrollableFilters>
          <S.Buttons>
            <Button decoration="secondary" text="Clear" onPress={clearFiltersAndCloseCallback} />
            <Button decoration="positive" text="Apply and Search" onPress={applyFiltersAndCloseCallback} />
          </S.Buttons>
        </S.DropdownContent>
      </S.Dropdown>
    </S.Container>
  )
}

export default CustomerSearch

const S = {
  Container: styled.div`
    white-space: nowrap;
  `,
  SearchContainer: styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
  `,
  FilterIcon: styled.button.withConfig({
    shouldForwardProp: (prop) => !['anyFilterSelected'].includes(prop)
  })<{ anyFilterSelected: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 95px;
    height: 39px;
    font-size: 16px;
    color: ${color('filterIcon')};
    border: ${color('filterIcon')} 1px solid;
    cursor: pointer;
    border-radius: 12px;
    font-family: Inter !important;
    padding: 10px;
    flex-direction: row-reverse;
    gap: 5px;
    background-color: ${color('paneBackground')};

    &&::before {
      font-size: 25px;
      font-family: 'Nexia-IcoFont';
    }

    ${({ anyFilterSelected }) => anyFilterSelected && `
      border-width: 2px;
    `}
  `,
  Search: styled(Search)<{ ariaLabel: string, placeholder: string, value: string, showClearIcon: boolean,
    onClickClearIcon: () => void, onChange: Dispatch<SetStateAction<string>>, onSearch: () => void, showSpinner: boolean, }>`
    display: inline-block;
    width: 500px;

    @media ${screenSize.small}{
      width: 100%;
    }
  `,
  Dropdown: styled(Dropdown)`
    width: 63%;
    margin-right: 30px;
    background: ${color('paneBackground')};
    border: 2px solid ${color('paneBackground')};
    border-radius: 8px;
    height: 80vh;

    @media ${screenSize.small} or ${screenSize.medium} {
      width: 96%;
    }
  `,
  DropdownContent: styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  ScrollableFilters: styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  `,
  Buttons: styled.div`
    display: flex;
    justify-content: center;
    gap: 20px;
    margin: 15px;
    background-color: ${color('paneBackground')};
    flex-shrink: 0;
  `
}
